import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Check, Eye, Loader2, Search, X, GitBranch } from "lucide-react";

const CAN_APPROVE = ["super_admin", "management", "qa_manager", "production_manager"];

export default function RecipeApprovals() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const canApprove = CAN_APPROVE.includes(role);
  const { data: raw, refetch } = useResource("/recipes/approvals");
  const rows = useMemo(() => (Array.isArray(raw) ? raw : []), [raw]);

  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");

  const filtered = rows.filter((r) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [r.recipeNumber, r.recipeId, r.name, r.productName, r.productId].some((v) => String(v || "").toLowerCase().includes(s));
  });

  const approve = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/recipes/${r.id}/approve`, {});
      toast.success(`${r.recipeNumber || "Recipe"} approved and set ACTIVE`);
      refetch();
    } catch (e) { toast.error(apiErrorMessage(e, "Approve failed")); }
    finally { setBusyId(""); }
  };

  const reject = async () => {
    if (!reason.trim()) return toast.error("A rejection reason is required");
    setBusyId(rejectFor.id);
    try {
      await api.post(`/recipes/${rejectFor.id}/reject`, { reason: reason.trim() });
      toast.success(`${rejectFor.recipeNumber || "Recipe"} rejected`);
      setRejectFor(null); setReason(""); refetch();
    } catch (e) { toast.error(apiErrorMessage(e, "Reject failed")); }
    finally { setBusyId(""); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Recipe Approvals" subtitle="Recipes submitted for review — approve to activate, or reject with a reason" />

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <Input data-testid="approvals-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipe, product…" className="h-9 pl-9" />
      </div>

      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="approvals-table">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-xs uppercase text-stone-500">
            <tr>{["Recipe ID", "Product", "Version", "Status", "Submitted", ""].map((h) => <th key={h} className="px-3 py-2.5">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-stone-400">
                <GitBranch className="mx-auto mb-2 h-6 w-6 text-stone-300" />No recipes pending approval.
              </td></tr>
            ) : filtered.map((r) => {
              const title = r.name || r.productName || "—";
              return (
                <tr key={r.id} data-testid={`approval-row-${r.recipeNumber || r.id}`} className="hover:bg-stone-50/60">
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-stone-700">{r.recipeNumber || r.recipeId || "—"}</td>
                  <td className="px-3 py-2 font-medium text-stone-800">
                    <button data-testid={`approval-view-${r.recipeNumber || r.id}`} className="text-left hover:text-amber-700 hover:underline" onClick={() => navigate(`/recipes/${r.id}`)}>{title}</button>
                    {r.productId && <div className="font-mono text-[10px] text-stone-400">{r.productId}</div>}
                  </td>
                  <td className="px-3 py-2 text-stone-600">V{r.version || 1}</td>
                  <td className="px-3 py-2"><StatusBadge value={r.status} /></td>
                  <td className="px-3 py-2 text-xs text-stone-500">{r.submittedAt ? new Date(r.submittedAt).toLocaleString("en-IN") : "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-stone-500" title="Open review" data-testid={`approval-open-${r.recipeNumber || r.id}`} onClick={() => navigate(`/recipes/${r.id}`)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canApprove && (
                        <>
                          <Button size="sm" className="h-7 gap-1 bg-emerald-600 px-2.5 text-xs hover:bg-emerald-700" data-testid={`approval-approve-${r.recipeNumber || r.id}`} disabled={busyId === r.id} onClick={() => approve(r)}>
                            {busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 gap-1 px-2.5 text-xs text-rose-600" data-testid={`approval-reject-${r.recipeNumber || r.id}`} disabled={busyId === r.id} onClick={() => { setRejectFor(r); setReason(""); }}>
                            <X className="h-3.5 w-3.5" /> Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!rejectFor} onOpenChange={(o) => { if (!o) { setRejectFor(null); setReason(""); } }}>
        <AlertDialogContent className="bg-white" data-testid="approval-reject-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reject {rejectFor?.recipeNumber || "recipe"}?</AlertDialogTitle>
            <AlertDialogDescription className="text-xs">Provide a reason. The recipe goes back as REJECTED and can be edited &amp; resubmitted.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea data-testid="approval-reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for rejection… (required)" className="min-h-[80px]" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="approval-reject-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="approval-reject-confirm" disabled={!reason.trim() || busyId} className="bg-rose-600 hover:bg-rose-700" onClick={reject}>Reject</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
